import { t } from "../lib/i18n";
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { getUserSettings, createCheckoutSession, redeemCoupon, getDashboardData, getStripeConfig } from '../api';
import Card from './Card';
import Tooltip from './Tooltip';
import Tabs, { Tab } from './Tabs'; // Import Tabs and Tab components
import RecentActivitySection from './RecentActivitySection'; // Import RecentActivitySection

let stripePromise: Promise<any> | null = null;

const creditPackages = [
    {
        name: "Starter Pack",
        credits: 125,
        price: "$50",
        packageName: "starter",
        description: "Get 125 credits. Perfect for getting started and trying out the core features."
    },
    {
        name: "Pro Pack",
        credits: 250,
        price: "$100",
        packageName: "pro",
        description: "Get 250 credits. Best value for regular users."
    },
    {
        name: "Business Pack",
        credits: 1700,
        price: "$500",
        packageName: "business",
        description: "Get 1700 credits. For power users and businesses, offering a 25% discount."
    },
    {
        name: "Enterprise Pack",
        credits: 5000,
        price: "$1000",
        packageName: "enterprise",
        description: "Get 5000 credits. The ultimate pack for institutional clients, with a massive 50% discount."
    }
];

export const Account: React.FC = () => {
    const [settings, setSettings] = useState<{ credits: number, leaseAbstractionClauses?: string[], has_made_purchase?: boolean }>({ credits: 0 });
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [couponCode, setCouponCode] = useState('');
    const [redeeming, setRedeeming] = useState(false);
    const [message, setMessage] = useState('');
    const [recentActivities, setRecentActivities] = useState<any[]>([]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const settingsData = await getUserSettings();
            setSettings({
                credits: settingsData.credits ?? 0,
                has_made_purchase: settingsData.has_made_purchase ?? false
            });
            // Fetch recent activities with a limit of 30
            const dashboardData = await getDashboardData(30);
            setRecentActivities(dashboardData.recent_activity || []);

        } catch (error) {
            console.error('Error fetching account data:', error);
            setMessage('Error fetching your account data.');
        }
        setLoading(false);
    };

    const location = useLocation();

    useEffect(() => {
        const query = new URLSearchParams(location.search);
        if (query.get('purchase_success')) {
            setMessage("Purchase successful! Your credits have been added.");
            fetchData();
            window.history.replaceState({}, document.title, window.location.pathname);
        } else if (query.get('purchase_canceled')) {
            setMessage("Your purchase was canceled. You have not been charged.");
            fetchData();
            window.history.replaceState({}, document.title, window.location.pathname);
        } else {
            fetchData();
        }
    }, [location.search]);

    const handleBuyCreditsClick = async (packageName: string) => {
        setSubmitting(true);
        setMessage('');

        if (!stripePromise) {
            try {
                const configRes = await getStripeConfig();
                if (configRes && configRes.publishableKey) {
                    stripePromise = loadStripe(configRes.publishableKey);
                } else {
                    throw new Error("Publishable key not returned from backend");
                }
            } catch (err) {
                console.error("Failed to load Stripe config:", err);
                setMessage("Billing is currently unavailable. Please try again later.");
                setSubmitting(false);
                return;
            }
        }

        const stripe = await stripePromise;
        if (!stripe) {
            setMessage("Stripe is not available. Please try again later.");
            setSubmitting(false);
            return;
        }

        try {
            const { sessionId } = await createCheckoutSession(packageName, window.location.origin);
            const { error } = await stripe.redirectToCheckout({ sessionId });

            if (error) {
                console.error("Error redirecting to checkout:", error);
                setMessage(`Failed to redirect to checkout: ${error.message}`);
            }
        } catch (error) {
            console.error("Error creating checkout session:", error);
            setMessage("Failed to start purchase process. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleRedeemCoupon = async (e: React.FormEvent) => {
        e.preventDefault();
        setMessage('');
        setRedeeming(true);
        try {
            const response = await redeemCoupon(couponCode);
            setMessage(response.message || `Successfully added ${response.credits_added} credits!`);
            setCouponCode('');
            fetchData();
        } catch (error: any) {
            console.error('Error redeeming coupon:', error);
            setMessage(error.response?.data?.detail || 'Failed to redeem coupon.');
        } finally {
            setRedeeming(false);
        }
    };

    if (loading) {
        return <div className="text-center p-8 text-text-secondary">{t("account.k9")}</div>;
    }

    return (
        <div className="container mx-auto p-4 bg-bg-primary min-h-screen">
            <div className="flex items-center mb-6">
                <h1 className="text-3xl font-bold text-text-secondary">{t("account.k10")}</h1>
                <Tooltip text="This page is your central hub for managing your account. Here you can view your current credit balance, purchase more credits, and redeem coupon codes." />
            </div>

            {message && <p className={`mb-4 text-center p-2 rounded ${message.includes('successful') || message.includes('added') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{message}</p>}

            <Tabs initialActiveTab="Credits">
                <Tab label="Credits">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                        <div className="md:col-span-1">
                            <h2 className="text-2xl font-bold mb-4 text-text-secondary">{t("account.k11")}</h2>
                            <p className="text-2xl font-bold text-text-primary">{settings.credits}</p>
                            {!settings.has_made_purchase && (
                                <p className="text-sm text-text-secondary mt-2">{t("account.k12")}</p>
                            )}
                            {settings.credits < 5 && (
                                <p className="text-sm text-text-secondary mt-2">{t("account.k13")}</p>
                            )}
                        </div>
                        <div className="md:col-span-2">
                            <form onSubmit={handleRedeemCoupon}>
                                <div className="mb-4">
                                    <label htmlFor="couponCode" className="block text-sm font-medium text-text-secondary mb-2">{t("account.k14")}</label>
                                </div>
                                <div>
                                    <input
                                        type="text"
                                        id="couponCode"
                                        className="shadow appearance-none border border-border-primary rounded w-full py-2 px-3 text-text-primary leading-tight focus:outline-none focus:shadow-outline"
                                        value={couponCode}
                                        onChange={(e) => setCouponCode(e.target.value)}
                                        disabled={redeeming}
                                        required
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={redeeming || !couponCode}
                                    className="w-full bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 mt-4"
                                >
                                    {redeeming ? 'Redeeming...' : t("account.k15")}
                                </button>
                            </form>
                        </div>
                    </div>

                    <div>
                        <h2 className="text-2xl font-bold mb-4 text-text-secondary">{t("account.k16")}</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {creditPackages.map((pkg) => (
                                pkg.packageName === "enterprise" ? (
                                    <div key={pkg.name} className="md:col-span-3">
                                        <Card>
                                            <div className="py-4 flex flex-col justify-between h-full">
                                                <div>
                                                    <h2 className="text-lg font-semibold mb-2">{pkg.name}</h2>
                                                    <p className="text-sm text-text-secondary">{pkg.description}</p>
                                                </div>
                                                <div className="text-right mt-4">
                                                    <p className="text-xl font-bold text-text-primary mb-2">{pkg.price}</p>
                                                    <button
                                                        onClick={() => handleBuyCreditsClick(pkg.packageName)}
                                                        disabled={submitting}
                                                        className="w-full bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
                                                    >
                                                        {submitting ? 'Processing...' : t("account.k17")}
                                                    </button>
                                                </div>
                                            </div>
                                        </Card>
                                    </div>
                                ) : (
                                    <Card key={pkg.name}>
                                        <div className="py-4 flex flex-col justify-between h-full">
                                            <div>
                                                <h2 className="text-lg font-semibold mb-2">{pkg.name}</h2>
                                                <p className="text-sm text-text-secondary">{pkg.description}</p>
                                            </div>
                                            <div className="text-right mt-4">
                                                <p className="text-xl font-bold text-text-primary mb-2">{pkg.price}</p>
                                                <button
                                                    onClick={() => handleBuyCreditsClick(pkg.packageName)}
                                                    disabled={submitting}
                                                    className="w-full bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
                                                >
                                                    {submitting ? 'Processing...' : t("account.k17")}
                                                </button>
                                            </div>
                                        </div>
                                    </Card>
                                )
                            ))}
                        </div>
                    </div>
                </Tab>

                <Tab label="Recent Activity">
                    <RecentActivitySection activities={recentActivities} limit={30} />
                </Tab>
            </Tabs>
        </div>
    );
};
